import React from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { AGENDA_CONDITIONS, AGENDA_CONDITIONS_TYPE } from 'screens/oneOnOneRabbit/SettingsPage/constants';
import { check1on1V2EventAgendaEnabled } from 'utils/helpers/featureFlags';

import { AddTask, Ellipsis, Trash } from 'assets/assetsV1/iconComponents';

import { Accordion, AccordionButton, AccordionItem, AccordionPanel } from 'uiToolkitV1/Accordion';
import { Menu } from 'uiToolkitV1/Menu';
import { TagColored } from 'uiToolkitV1/Tag';
import BetaTag from 'uiToolkitV1/Tag/BetaTag';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { SettingsCardTemplate } from '../../components/common/SettingsCardTemplate';
import { OneOnOneSettingsPageReactQueryKeys } from '../../constants/OneOnOneSettingsPageReactQueryKeys';
import { deleteOneOnOneEvent, setSaveButtonIsDisabled } from '../../slice/OneOnOneEventBasedAgendaSlice';
import { Events } from '../Events/Events';
import { AgendaProps } from './components/AdminAgendaItem';
import { AgendaList } from './components/AgendaList';
import { useOneOnOneEventTemplateAgenda, useOneOnOneRegularTemplateAgenda } from './hooks/dataHooks';

interface Props {}

const AgendaPoints = (props: Props) => {
  const { data: regularAgendaData, isLoading: isLoadingRegularAgendaData } = useOneOnOneRegularTemplateAgenda();

  const { data: eventAgendaData, isLoading: isLoadingEventAgendaData } = useOneOnOneEventTemplateAgenda();

  const events = Object.keys(eventAgendaData || {});

  const dispatch = useDispatch();

  const queryClient = useQueryClient();

  const regularAgendaLength = regularAgendaData?.filter(({ isDeleted }) => !isDeleted)?.length ?? 0;

  return (
    <SettingsCardTemplate
      heading="Define Agenda Points"
      subHeading="Add agenda points here to ensure they appear across all manager and direct team member/skip team member meetings."
      Icon={AddTask}
      hasButton
      showDivider
      buttonComp={check1on1V2EventAgendaEnabled() && <Events />}
    >
      <Accordion mt="-20px" defaultIndex={0} allowMultiple allowToggle boxShadow="none !important">
        <AccordionItem
          sx={{
            button: {
              background: 'white',
            },
            '.chakra-accordion__button': {
              padding: '0px 4px',
              height: '72px',
            },
          }}
        >
          <AccordionButton>
            <Flex w="100%" alignItems="center">
              <Flex alignItems="baseline" flexDirection="column" ml="8px">
                <TextBodyMedium>Regular 1:1</TextBodyMedium>
                {/* <TextLabel color="#666666">
                  Add Agenda points for routine 1:1s between manager and their direct reports
                </TextLabel> */}
              </Flex>
              {/* @ts-ignore */}
              <Box
                ml="auto"
                className="info"
                sx={{
                  p: {
                    fontWeight: 'normal',
                  },
                }}
                mr="58px"
              >
                {' '}
                <TagColored color="black">
                  {regularAgendaLength} Agenda {regularAgendaLength > 1 ? 'Points' : 'Point'}
                </TagColored>
              </Box>
            </Flex>
          </AccordionButton>
          <AccordionPanel p="0px 4px !important" borderTop="0 !important">
            <AgendaList agendaCondition="REGULAR" agenda={regularAgendaData} />
          </AccordionPanel>
        </AccordionItem>
        {events.map((event: AGENDA_CONDITIONS_TYPE) => {
          const eventAgendaLength = eventAgendaData[event]?.filter(({ isDeleted }) => !isDeleted)?.length ?? 0;

          const menuOptions = [
            {
              label: 'Delete Event',
              onClick: (e) => {
                dispatch(
                  deleteOneOnOneEvent({
                    eventIdToBeDeleted: event,
                  })
                );
                dispatch(
                  setSaveButtonIsDisabled({
                    saveButtonIsDisabled: false,
                  })
                );

                queryClient.setQueryData<
                  Array<
                    AgendaProps & {
                      uiId: string;
                    }
                  >
                >([OneOnOneSettingsPageReactQueryKeys.oneOnOneAdminAgenda, 'EVENT_BASED'], (oldData) => {
                  const newData = { ...oldData };
                  delete newData[event];
                  return newData;
                });
              },
              Icon: Trash,
            },
          ];

          return (
            <AccordionItem
              key={event}
              sx={{
                button: {
                  background: 'white',
                },
                '.chakra-accordion__button': {
                  padding: '0px 4px',
                  height: '72px',
                },
              }}
            >
              <Flex alignItems="center">
                <AccordionButton>
                  <Flex w="100%" alignItems="center">
                    <Flex alignItems="center" ml="8px" gap={3}>
                      <TextBodyMedium>{AGENDA_CONDITIONS[event]} </TextBodyMedium>
                      {event !== AGENDA_CONDITIONS.REGULAR && <BetaTag />}
                      {/* <TextLabel color="#666666">
                        Add Agenda points for routine 1:1s between manager and their direct reports
                      </TextLabel> */}
                    </Flex>
                    {/* @ts-ignore */}
                    <Box
                      ml="auto"
                      className="info"
                      sx={{
                        p: {
                          fontWeight: 'normal',
                        },
                      }}
                    >
                      <TagColored color="black">
                        {eventAgendaLength ?? 0} Agenda {eventAgendaLength > 1 ? 'Points' : 'Point'}
                      </TagColored>
                    </Box>
                  </Flex>
                </AccordionButton>
                <Menu
                  sx={{ background: 'transparent !important' }}
                  // @ts-ignore
                  menuList={menuOptions}
                  MenuIcon={Ellipsis}
                />
              </Flex>
              <AccordionPanel p="0px 4px !important" borderTop="0 !important">
                <AgendaList agendaCondition={event} agenda={eventAgendaData[event]} />
              </AccordionPanel>
            </AccordionItem>
          );
        })}
      </Accordion>
    </SettingsCardTemplate>
  );
};

export { AgendaPoints };
